import { certPOSTj } from "lib/API";
import { useEffect, useRef, useState } from "react";
import { collect } from "react-recollect";

function CheckDataElement({ fromDB, fromCert, k, l }) {
  if (
    [
      "_id",
      "suspendedBy",
      "suspendedReason",
      "suspendedTimestamp",
      "origCertNumber",
    ].includes(k)
  )
    return null;
  if (l === "_") return null;

  // these shouldn't happen
  if (!k)
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-red-600">No key!</td>
        <td></td>
      </tr>
    );
  if (!fromDB)
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-right pr-2">{k}</td>
        <td className="text-red-600">no fromDB</td>
      </tr>
    );
  if (!fromCert)
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-right pr-2">{k}</td>
        <td className="text-red-600">no fromCert</td>
      </tr>
    );

  // no data when there should be (# and / are optional)
  if ((!fromDB[k] || !fromCert[k]) && !l.startsWith("#") && !l.startsWith("/"))
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-right pr-2">{k}</td>
        <td className="text-red-600">
          {fromDB[k] ? (
            <p>from DB: {fromDB[k]}</p>
          ) : (
            <p className="text-red-600">no data in system</p>
          )}
          {fromCert[k] ? (
            <p>from Cert: {fromCert[k]}</p>
          ) : (
            <p className="text-red-600">no data in certificate</p>
          )}
        </td>
      </tr>
    );

  // doesn't match!!
  if (fromDB[k] !== fromCert[k])
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-right pr-2">{k}</td>
        <td className="text-red-600">
          no match! "{fromCert[k]}" from certificate does not match system data
          "{fromDB[k]}"
        </td>
      </tr>
    );

  // additional cert data that we don't know what to do with
  if (l.startsWith("?"))
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-right pr-2">{k}</td>
        <td className="text-yellow-500">{fromCert[k]}</td>
      </tr>
    );

  // ---- VALID! -----

  // information only
  if (l.startsWith("#"))
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-right pr-2 opacity-50">{l.substring(2)}</td>
        <td className="text-slate-500 opacity-50">{fromCert[k]}</td>
      </tr>
    );
  // linky
  if (l.startsWith("/"))
    return (
      <tr className="border-t border-t-slate-400">
        <td className="text-right pr-2">{l.substring(2)}</td>
        <td className="text-slate-500">
          <a href={fromCert[k]} target="_blank" rel="noreferrer">
            {fromCert[k]}
          </a>
        </td>
      </tr>
    );
  // normal valid data
  return (
    <tr className="border-t border-t-slate-100">
      <td className="text-right pr-2">{l}</td>
      <td className="text-green-600">✓ {fromDB[k]}</td>
    </tr>
  );
}

function CheckBcrypt({ b, d, filename, pdfAsDataURI }) {
  const [r, setR] = useState();

  useEffect(() => {
    certPOSTj("checkSig", { b, d }).then(setR);
  }, [b, d]);

  // const [dataURIHash, setDataURIHash] = useState();
  // useEffect(() => {
  //   async function doHash() {
  //     setDataURIHash(await createHash(pdfAsDataURI));
  //   }
  //   doHash();
  // }, [pdfAsDataURI]);

  if (!r) return <p>Checking cryptographic signature is valid&hellip;</p>;
  if (!r.researchData)
    return (
      <p className="text-pink-500 p-2 text-lg">
        No researchData (maybe you're on dev checking a prod certificate?)
      </p>
    );

  const { data } = JSON.parse(atob(d));

  const dataKeyOrdering = [
    { key: "certNumber", label: "Certificate Number" },
    { key: "makeAndModel", label: "Make & Model" },
    { key: "vin", label: "VIN" },
    { key: "engineNumber", label: "Engine Number" },
    { key: "bodyNumber", label: "Body Number" },
    { key: "spec", label: "Specification" },
    { key: "extColour", label: "Exterior Colour" },
    { key: "trimColour", label: "Trim Colour" },
    { key: "hoodColour", label: "Hood Colour" },
    { key: "dateBuild", label: "Date(s) built" },
    { key: "dateDespatched", label: "Date despatched" },
    { key: "dest", label: "Destination (dealer)" },
    { key: "otherNumbers", label: "Other numbers" },
    { key: "details", label: "Details of Equipment" },
    { key: "other", label: "Other information" },
    { key: "_certId", label: "# Internal ID" },
    { key: "copiedFromPrevCert", label: "# Copied from" },
    { key: "issuedTo", label: "# Issued To" },
    { key: "dateOfIssue", label: "# Date of Issue" },
    { key: "searchHelper", label: "_" },
    { key: "shopifyLink", label: "/ Shopify Link" },
  ];

  let checkDataKeysOrdered = [
    ...dataKeyOrdering.map((kl) => ({ key: kl.key, label: kl.label })),
    ...Object.keys(data).map((k) => ({
      key: k,
      label: `? ${k}`,
      order: dataKeyOrdering.findIndex((dk) => dk.key === k),
    })),
  ];

  return (
    <div className="flex w-full h-full">
      <div className="pr-2 max-w-lg">
        <p className="text-slate-700">
          Selected file: <span className="font-semibold">{filename}</span>
        </p>

        {/* <pre>{JSON.stringify({ dataURIHash })}</pre> */}

        <p>
          Certificate signature is{" "}
          {r.r ? (
            <span className="text-green-600">VALID</span>
          ) : (
            <span className="text-red-600">INVALID</span>
          )}
          , but running further checks
        </p>
        <p className="mt-2">
          Checking if data from system matches data from certificate:
        </p>
        <table>
          <thead>
            <tr>
              <th className="text-right pr-2">Key</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {checkDataKeysOrdered.map((klo) =>
              klo.order >= 0 ? (
                <CheckDataElement
                  fromDB={r.researchData}
                  fromCert={data}
                  k={klo.key}
                  l={klo.label}
                  key={klo.key}
                />
              ) : null,
            )}
          </tbody>
        </table>
        <p>
          <i>
            Manually check if the text on the certificate matches this data!
          </i>
        </p>
        <pre className="mt-4 w-full overflow-scroll max-w-3xl bg-gray-900 text-green-400 text-sm break-all opacity-5 hover:opacity-100">
          {JSON.stringify(r, null, 2)}
        </pre>
      </div>{" "}
      <div className="flex-1">
        <embed
          src={pdfAsDataURI}
          type="application/pdf"
          className="w-full h-full"
        />
      </div>
    </div>
  );
}

const ProcessPDF = collect(function ({ fileContent, filename }) {
  const decoder = new TextDecoder("utf-8");
  const fileText = decoder.decode(fileContent);

  if (fileContent?.length < 100) return <p>File is too small!</p>;

  const ssig = fileText.indexOf("certSystemSig");
  if (ssig < 1)
    return (
      <p>
        No BMIHT cryptographic signature found! This probably means that the
        certificate has been edited
      </p>
    );
  const esig = fileText.indexOf("\n", ssig);
  if (esig < 1) return <p>No end of signature found!</p>;
  const sig = fileText.substring(ssig, esig);
  if (sig.length < 100)
    return <p>Incorrect signature found (length is only {sig.length}</p>;

  const sigparts = sig.split("-");
  if (sigparts.length !== 3)
    return (
      <p>
        Incorrect signature found! Should be in 3 parts, is in {sigparts.length}{" "}
        instead.
      </p>
    );

  let b = sigparts[1];
  if (b.length < 10) return <p>Incorrect signature - only {b.length} long</p>;
  if (b[0] !== "$") b = atob(b);
  if (b[0] !== "$" || b[3] !== "$" || b[6] !== "$")
    return <p>Incorrect signature - bcrypt start {b.substring(0, 7)}</p>;

  const d = sigparts[2];
  if (!d) return <p>No buffer?</p>;

  // convert to base64 so we can embed into the html
  function _arrayBufferToBase64(buffer) {
    var binary = "";
    var bytes = new Uint8Array(buffer);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  const pdfAsBase64 = `${_arrayBufferToBase64(fileContent)}`;
  const pdfAsDataURI = `data:application/pdf;base64,${pdfAsBase64}`;

  return (
    <CheckBcrypt b={b} d={d} filename={filename} pdfAsDataURI={pdfAsDataURI} />
  );
});

export default function Verify() {
  // State variables for managing the file, its content, and UI messages
  const [selectedFile, setSelectedFile] = useState(null);
  const fileContentRef = useRef();
  const fileContent = fileContentRef.current;
  const setFileContent = (fc) => (fileContentRef.current = fc);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingFileData, setIsLoadingFileData] = useState(false);

  // Handle file selection from the input
  const handleFileChange = (event) => {
    // Clear previous states
    setSelectedFile(null);
    setFileContent("");
    setErrorMessage("");

    // Get the first file from the input
    const file = event.target.files[0];

    // Check if a file was selected
    if (file) {
      // Validate that the selected file is a PDF
      if (file.type === "application/pdf") {
        setSelectedFile(file);
        // Start the loading state
        setIsLoadingFileData(true);

        // Create a FileReader instance to read the file content
        const reader = new FileReader();

        // This event handler is triggered when the file is successfully read
        reader.onload = (e) => {
          // Get the raw file data as an ArrayBuffer
          const content = e.target.result;
          // Store the content in state
          setFileContent(content);
          // End the loading state
          setIsLoadingFileData(false);
        };

        // This event handler is triggered if an error occurs during reading
        reader.onerror = () => {
          setIsLoadingFileData(false);
          setErrorMessage("Failed to read the file.");
        };

        // Read the file as an ArrayBuffer
        // This is a good format for PDFs as it preserves the binary data
        reader.readAsArrayBuffer(file);
      } else {
        setErrorMessage("Please select a PDF file.");
      }
    }
  };

  return (
    <div className="bg-white p-8 h-full flex flex-col">
      <h1 className="text-3xl font-semibold text-slate-800 mb-4">
        Digital Certificate Verifier™
      </h1>

      {/* File Input */}
      <label
        htmlFor="file-upload"
        className="cursor-pointer bg-slate-600 w-64 text-white text-center font-semibold py-2 px-4 rounded-xl shadow-md hover:bg-slate-700 transition duration-300 ease-in-out"
      >
        Choose PDF File
      </label>
      <input
        id="file-upload"
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Display selected file name and status */}
      <div className="mt-6 h-full">
        {isLoadingFileData && (
          <p className="text-slate-500 mt-2">
            Reading and decoding file&hellip;
          </p>
        )}

        {errorMessage && (
          <p className="text-red-500 mt-2 font-medium">Error: {errorMessage}</p>
        )}

        {fileContent && (
          <div className="h-full">
            <ProcessPDF
              fileContent={fileContent}
              filename={selectedFile.name}
            />
          </div>
        )}
      </div>
    </div>
  );
}
